import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './AdminDashboard.css';
import {
    AdminDashboardData,
    DEFAULT_PERIOD,
    Period,
    fetchAdminDashboardData,
    isPeriod,
} from '../../api/adminDashboard';
import ConnectorsCard from './components/ConnectorsCard';
import ConversationsCard from './components/ConversationsCard';
import DashboardSkeleton from './components/DashboardSkeleton';
import GoalCompletionCard from './components/GoalCompletionCard';
import HoursSavedCard from './components/HoursSavedCard';
import MetricCard from './components/MetricCard';
import PeriodSelect from './components/PeriodSelect';
import TopPerformersCard from './components/TopPerformersCard';
import UsageTrendsCard from './components/UsageTrendsCard';

const AdminDashboard: React.FC = () => {
    const { getAccessTokenSilently } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState<AdminDashboardData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const periodParam = searchParams.get('period');
    const period: Period = isPeriod(periodParam) ? periodParam : DEFAULT_PERIOD;

    useEffect(() => {
        let isActive = true;
        setIsLoading(true);
        getAccessTokenSilently()
            .then((token) => fetchAdminDashboardData(period, token))
            .then((result) => {
                if (isActive) {
                    setData(result);
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (isActive) {
                    setIsLoading(false);
                }
            });

        return () => {
            isActive = false;
        };
    }, [period, getAccessTokenSilently]);

    const handlePeriodChange = (next: Period) => {
        setSearchParams({ period: next }, { replace: true });
    };

    return (
        <div className="ad-dashboard-container">
            <div className="ad-dashboard-header">
                <h2>AI Adoption Dashboard</h2>
                <PeriodSelect value={period} onChange={handlePeriodChange} />
            </div>

            {isLoading || !data ? (
                <DashboardSkeleton />
            ) : (
                <div className="ad-grid">
                    <HoursSavedCard hoursSaved={data.hoursSaved} />
                    <UsageTrendsCard usageTrends={data.usageTrends} />
                    <ConversationsCard conversations={data.conversations} />
                    <GoalCompletionCard goalCompletion={data.goalCompletion} />
                    <TopPerformersCard topPerformers={data.topPerformers} />
                    <MetricCard
                        title="Documents Accessed"
                        metric={data.documentsAccessed}
                        className="ad-col-documents"
                    />
                    <MetricCard
                        title="Tokens Consumed"
                        metric={data.tokensConsumed}
                        className="ad-col-tokens"
                    />
                    <ConnectorsCard connectors={data.connectors} />
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
